import React, { useEffect, useState } from 'react';
import "@marshal/chatbox";
import axios from '../../../../axios';
import ChatbotDisclaimer from './ChatbotDisclaimer';

const HEADERS = {
    clientId: 'x-marshal-client-id',
    marshalEnv: 'x-marshal-env',
    moduleId: 'x-marshal-module-id',
    cookie: 'x-marshal-cookie',
    currentmarket: 'currentmarket',
    loggedinuserid: 'loggedinuserid',
};

const parseCookie = (str) =>
    str
        .split(';')
        .map((v) => v.split('='))
        .reduce((acc, v) => {
            acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1].trim());
            return acc;
        }, {});

export default () => {
    const [config, setConfig] = useState(null);
    const [ready, setReady] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const commonHeaders = {};

    useEffect(() => {
        const cookie = parseCookie(document.cookie);
        const init = async () => {
            axios
                .get('/api/gateway/ccm/app/marshalConfig', {
                    headers: {
                        loggedinuserid: 'app',
                        currentmarket: 'us',
                    },
                })
                .then(({ data }) => {
                    setConfig(data);
                    axios.defaults.headers.common[HEADERS.clientId] = data[HEADERS.clientId];
                    axios.defaults.headers.common[HEADERS.marshalEnv] = data[HEADERS.marshalEnv];

                    /** For client authentication */
                    axios.defaults.headers.common[HEADERS.cookie] = `authToken=${encodeURIComponent(
                        cookie['authToken'],
                    )}`;
                    commonHeaders['currentmarket'] = cookie['currentMarket'];
                    commonHeaders['loggedinuserid'] = cookie['loggedInUserName'];
                });
        };
        init();
    }, []);

    function getChatbotPath(path) {
        return `${config.base_url}${config.base_path}${path}`;
    }

    async function getResponse(input, module) {
        try {
            const { data } = await axios.post(
                getChatbotPath(`/chat`),
                { message: input },
                {
                    headers: {
                        [HEADERS.moduleId]: module,
                        ...commonHeaders,
                    },
                },
            );
            return {
                status: true,
                data: {
                    message: data.data['output'],
                    id: Date.now().toString(),
                    type: 'ai',
                },
            };
        } catch (e) {
            console.error(e);
        }
        return {
            status: false,
            data: [],
        };
    }

    async function getChipset() {
        try {
            const { data } = await axios.get(getChatbotPath(`/chat/init`), { headers: commonHeaders });
            return {
                status: true,
                data: {
                    context: data.data.context,
                    modules: data.data.modules.map((e) => {
                        return {
                            id: e.id,
                            label: e.name,
                        };
                    }),
                },
            };
        } catch (e) {
            console.error(e);
        }
        return {
            status: false,
            data: [],
        };
    }

    async function getMessages() {
        try {
            const { data } = await axios.get(getChatbotPath(`/chat/history`), { headers: commonHeaders });
            return {
                status: true,
                data: data.data.messages,
            };
        } catch (e) {
            console.error(e);
        }
        return {
            status: false,
            data: [],
        };
    }

    async function setContext(moduleId) {
        try {
            await axios.patch(getChatbotPath(`/chat/reset?context=${moduleId}`), {}, { headers: commonHeaders });
            return {
                status: true,
            };
        } catch (e) {
            console.error(e);
        }
        return {
            status: false,
            data: [],
        };
    }

    useEffect(() => {
        if (!config) return;
        window.addEventListener('marshal-on-ready', (event) => {
            const chatbox = event.detail.chatbox;
            chatbox.getResponse = getResponse;
            chatbox.getChipset = getChipset;
            chatbox.getMessages = getMessages;
            chatbox.setContext = setContext;
            window.dispatchEvent(new CustomEvent('marshal-show-chipset'));
        });
        setReady(true);
    }, [config]);

    function isEpochToday(epochTimestamp) {
        try {
            const today = new Date();
            const dateFromEpoch = new Date(parseInt(epochTimestamp));
            return (
                today.getFullYear() === dateFromEpoch.getFullYear() &&
                today.getMonth() === dateFromEpoch.getMonth() &&
                today.getDate() === dateFromEpoch.getDate()
            );
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    // If shown today, dont show
    // If never shown, show
    // If not shown today, show
    function shouldShowDisclaimer() {
        const wasShown = localStorage.getItem('chatbot-disclaimer');
        if (!wasShown || !isEpochToday(wasShown)) {
            localStorage.setItem('chatbot-disclaimer', Date.now());
            return true;
        }
        return false;
    }

    useEffect(() => {
        const state = shouldShowDisclaimer();
        if (!showDisclaimer) setShowDisclaimer(state); // Only if not already showing, change state.
    }, [ready]);

    return (
        <React.Fragment>
            {showDisclaimer && (
                <ChatbotDisclaimer modalShow={showDisclaimer} setModalShow={setShowDisclaimer}></ChatbotDisclaimer>
            )}
            <div style={{ justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                {!config && (
                    <div style={{ height: '100%', minHeight: '100%', position: 'relative', top: '50%' }}>
                        Fetching Configurations...
                    </div>
                )}
                {ready && <marshal-chatbox id="marshal-chatbox" client="gscope"></marshal-chatbox>}
            </div>
        </React.Fragment>
    );
};
